import { formatFileSize } from "./utils.js";
export class UploadClient {
    static _instance = null;
    currentUploadId = null;
    CHUNK_SIZE = 1024 * 1024; // 1MB per chunk
    static getInstance(options) {
        if (!this._instance) {
            this._instance = new UploadClient(options);
        }
        return this._instance;
    }
    routeNames;
    setRouteNames(routeNames) {
        this.routeNames = {
            ...this.routeNames,
            ...routeNames
        };
    }
    options;
    constructor(options) {
        const defaultOptions = {
            routeNames: {
                start: '/api/upload/start',
                chunk: '/api/upload/chunk',
                finish: '/api/upload/finish',
                cancel: '/api/upload/cancel'
            },
            onLog: console.log,
            onStart: (uploadId) => console.log(`Upload started with ID: ${uploadId}`),
            onError: (error) => console.error(`Upload error: ${error}`),
            onComplete: (result) => console.log(`Upload completed: ${result.filename} (${formatFileSize(result.finalSize)})`)
        };
        // Merge completo delle opzioni
        this.options = {
            ...defaultOptions,
            ...options,
        };
        this.routeNames = {
            ...defaultOptions.routeNames,
            ...options.routeNames
        };
    }
    async fetchApi(path, config = {}) {
        const endpoint = this.routeNames[path];
        const url = new URL(endpoint, window.location.origin);
        // Aggiungi query parameters se presenti
        if (config.queryParams) {
            Object.entries(config.queryParams).forEach(([key, value]) => {
                url.searchParams.set(key, value.toString());
            });
        }
        // Determina il body e headers
        let body;
        const headers = {};
        if (config.formData) {
            body = config.formData;
            // Non settare Content-Type per FormData (browser lo fa automaticamente)
        }
        else if (config.jsonBody) {
            body = JSON.stringify(config.jsonBody);
            headers['Content-Type'] = 'application/json';
        }
        const defaultOptions = {
            method: 'POST',
            headers,
            body
        };
        const finalOptions = {
            ...defaultOptions,
            ...config.options,
            headers: {
                ...headers,
                ...config.options?.headers
            }
        };
        try {
            const response = await fetch(url.toString(), finalOptions);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            return { success: true, data };
        }
        catch (error) {
            console.error(`Error fetching ${path}:`, error);
            return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    async fetchUploadStart(data) {
        return await this.fetchApi('start', {
            jsonBody: data
        });
    }
    async fetchUploadChunk(queryParams, chunkBlob) {
        const formData = new FormData();
        formData.append('chunk', chunkBlob);
        return await this.fetchApi('chunk', {
            queryParams: {
                uploadId: queryParams.uploadId,
                chunkIndex: queryParams.chunkIndex,
                chunkSize: queryParams.chunkSize
            },
            formData
        });
    }
    async fetchUploadFinish(data) {
        return await this.fetchApi('finish', {
            jsonBody: data
        });
    }
    async fetchUploadCancel(data) {
        return await this.fetchApi('cancel', {
            jsonBody: data
        });
    }
    async upload(file) {
        try {
            this.options?.onLog?.(`Inizializzazione upload per: ${file.name}`);
            // 1. Start upload
            const startResult = await this.fetchUploadStart({
                filename: file.name,
                mimeType: file.type,
                totalSize: file.size
            });
            if (!startResult.success || !startResult.data) {
                throw new Error(startResult.error || 'Errore nell\'inizializzazione upload');
            }
            this.currentUploadId = startResult.data.uploadId;
            this.options?.onStart?.(this.currentUploadId);
            this.options?.onLog?.(`Upload iniziato con ID: ${this.currentUploadId}`);
            // 2. Upload chunks
            await this.uploadFileInChunks(file, this.currentUploadId);
            // 3. Finish upload
            const finishResult = await this.fetchUploadFinish({
                uploadId: this.currentUploadId
            });
            if (!finishResult.success || !finishResult.data) {
                throw new Error(finishResult.error || 'Errore nella finalizzazione upload');
            }
            const result = {
                filename: finishResult.data.filename,
                finalSize: finishResult.data.finalSize,
                finalType: finishResult.data.finalType,
                finalPath: finishResult.data.finalPath,
                id: finishResult.data.id ?? ""
            };
            this.currentUploadId = null;
            this.options?.onComplete?.(result);
            this.options?.onLog?.(`Upload completato: ${result.filename} (${formatFileSize(result.finalSize)})`);
            return result;
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
            this.options?.onError?.(errorMessage);
            this.currentUploadId = null;
            throw error;
        }
    }
    async uploadFileInChunks(file, uploadId) {
        const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);
        this.options?.onLog?.(`Invio di ${totalChunks} chunk da ${formatFileSize(this.CHUNK_SIZE)} ciascuno`);
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * this.CHUNK_SIZE;
            const end = Math.min(start + this.CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);
            this.options?.onLog?.(`Invio chunk ${chunkIndex + 1}/${totalChunks} (${formatFileSize(chunk.size)})`);
            // Leggi il chunk come ArrayBuffer
            const arrayBuffer = await this.readFileAsArrayBuffer(chunk);
            const chunkBlob = new Blob([arrayBuffer]);
            const chunkResult = await this.fetchUploadChunk({
                uploadId,
                chunkIndex,
                chunkSize: chunk.size
            }, chunkBlob);
            if (!chunkResult.success || !chunkResult.data) {
                throw new Error(chunkResult.error || `Errore upload chunk ${chunkIndex + 1}`);
            }
            // Callback per progress
            this.options?.onProgress?.({
                progress: chunkResult.data.progress,
                uploadedSize: chunkResult.data.uploadedSize,
                totalSize: chunkResult.data.totalSize,
                uploadId: chunkResult.data.uploadId
            });
            // Callback per chunk completato
            this.options?.onChunkComplete?.({
                chunkIndex,
                totalChunks,
                chunkSize: chunk.size,
                uploadId
            });
            this.options?.onLog?.(`Chunk ${chunkIndex + 1}/${totalChunks} inviato - Progresso: ${chunkResult.data.progress}%`);
        }
    }
    readFileAsArrayBuffer(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
        });
    }
    async cancel(uploadId) {
        const targetUploadId = uploadId || this.currentUploadId;
        if (!targetUploadId)
            return;
        try {
            this.options?.onLog?.(`Cancellazione upload ${targetUploadId}`);
            const cancelResult = await this.fetchUploadCancel({
                uploadId: targetUploadId
            });
            if (!cancelResult.success) {
                this.options?.onLog?.(`Attenzione: errore durante la cancellazione: ${cancelResult.error}`);
            }
            if (targetUploadId === this.currentUploadId) {
                this.currentUploadId = null;
            }
            this.options?.onCancel?.();
            this.options?.onLog?.('Upload cancellato');
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
            this.options?.onLog?.(`Errore durante la cancellazione: ${errorMessage}`);
            this.options?.onError?.(errorMessage);
        }
    }
}
